// Which branches the portal offers, and where that answer comes from. P6-83.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 DERIVED FROM THE COMMAND SURFACE, NOT LISTED HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// The handoff's whole claim for the selector is *"adding a feature group =
// adding one pill + one panel; no layout change."* The other half of that claim
// is the one nobody writes down: REMOVING a feature has to cost nothing either.
//
// `content/commands.json` carries a `features` list generated by
// `scripts/generate-content.mjs` out of `content-src/command-catalog.json` (and
// the music service's own catalog), which are exported from the live cog
// surface. So a cog that goes into `SHELVED_COGS` stops registering commands,
// leaves the catalog on the next export, and its pill disappears from this page
// with no edit here — and comes back the same way. That is exactly what
// *"revisit closer to the NBA season"* needs.
//
// ⚠️ A PILL POINTING AT A FEATURE WHOSE COMMANDS ARE NOT REGISTERED IS A CLAIM
// ABOUT SOMETHING THAT IS NOT THERE. `bot.py`'s `SHELVED_COGS` is what CLAUDE.md
// says to derive the bot's feature set from, and this list is that decision
// arriving on the site rather than a second opinion about it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 AND A DERIVED LIST CAN SHRINK SILENTLY — SO THE HOLE IS WHAT FAILS
// ═══════════════════════════════════════════════════════════════════════════
//
// CLAUDE.md gotcha 1, from P4-12: a gate that derived its subject list stayed
// green while the list quietly halved, because a sibling still contributed the
// common name. *"Deriving instead of hardcoding removes the stale-list failure
// and introduces a quieter one — the derivation itself going wrong — so a
// derived gate needs a check that its DERIVATION is complete, not just that its
// output is non-empty."*
//
// So every feature in the catalog is either a BRANCH or an entry in
// `NOT_A_BRANCH` with a reason, and `src/test/portal/branches.test.js` fails on
// anything that is neither. A new cog lands as a HOLE — somebody has to decide
// whether it belongs on the portal — rather than as a silent omission.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE PANEL'S WORDS COME FROM THE CATALOG TOO
// ═══════════════════════════════════════════════════════════════════════════
//
// A stub panel is a heading and a paragraph, and the design's drafts of those
// carried DATA — *"212 in the canon"*, *"Preseason opens 3 Oct"*, *"Five-a-side,
// Saturday"*. Not one of those has a projection behind it: spec §3 says v1
// publishes movie data only, and music and the rest are named as LATER
// consumers. Inventing them here is the thing this whole page is built not to
// do.
//
// The catalog's own `label` and `blurb` are real, generated, and move when the
// cogs move — so a stub says what the feature IS, in the words the command
// reference already uses, and says plainly that nothing from it is on this page.

/**
 * The branches this page has a panel for, in the order the selector shows them.
 *
 * `feature` names the catalog key this branch's existence is tied to. A branch
 * whose feature has left the catalog is not offered.
 *
 * 🔴 MOVIE NIGHT IS FIRST AND IT IS THE DEFAULT — the handoff's instruction, and
 * it is the only branch with a projection behind it.
 */
export const BRANCHES = Object.freeze([
  // P6-316: the page has been live since P6-104; the panel's link is read from this, not typed.
  Object.freeze({
    id: 'movie',
    feature: 'movie',
    label: 'Movie night',
    kind: 'movie',
    docSection: 'docs-movie-night',
  }),
  // 🔴 JORDAN, 2026-09-22: *"I want to reorder the pills in the portal to have
  // "Watch" be next to the movie tab, followed by the music pill."* P6-278 moved
  // this entry and the one below it here, ahead of trips/events/hoops, and
  // nothing else about either changed. Reading top to bottom, this is now the
  // SECOND branch with a projection behind it — `music` below is the third; see
  // that entry for why the ordinal is not the landing order (P6-186 shipped
  // before W2b).
  // `utils/watch_projection.py` publishes `3a/watch.json` under the same
  // `utils/projection_publisher.py` spine the movie producer uses (bot-owned,
  // `PROJECTIONS` binding — see `functions/_lib/projections.js`'s header on
  // `readWatchProjection` for why this one has no music-shaped second
  // bucket). `docSection` is `docs-watch` since P6-302 wrote that page
  // (`content-src/sections.json`'s own `docs-watch` entry). This branch carried
  // none until then for exactly one reason: the page did not exist, and
  // pointing at a section id nobody had published would have been a broken
  // link masquerading as a working one. `trips` and `music` carry theirs for
  // the same reason the other way round -- their pages exist.
  //
  // ✅ P6-316: THE FIELD DRAWS A LINK NOW. It was correct data nothing read —
  // `PortalHome.jsx` handed `docPath` to `MusicPanel` and `StubPanel` only, so the
  // comment that stood here said no "docs" link appeared on this panel, and no
  // test read `docSection` at all to say otherwise. `PortalHome.jsx` derives
  // `docPath` from this field once and hands it to `WatchPanel`, which draws it
  // as *How the watch list works →*; `src/test/portal/doc-links.test.jsx` pins
  // that, and `documentation.test.jsx` fails a branch whose feature has a page
  // and no field. The words follow the page: `/docs/watch` opens by calling the
  // feature the crew's shared list, and *How watch works* reads as a verb phrase.
  Object.freeze({
    id: 'watch',
    feature: 'watch',
    label: 'Watch',
    kind: 'watch',
    docSection: 'docs-watch',
  }),
  // 🔴 P6-186 (M2), reordered by P6-278 to sit right after `watch` rather than
  // right after `hoops` — nothing else about this entry changed. Reading top to
  // bottom this is now the THIRD branch with a projection behind it, and it
  // remains the first one written by a deployable other than the bot:
  // `music/utils/projection.py` publishes `3a/music.json` under MI3, which
  // holds principle 2 PER OBJECT. `docSection` still points at the reference,
  // because the panel is a window on the room and not a manual for it.
  Object.freeze({
    id: 'music',
    feature: 'music',
    label: 'Music',
    kind: 'music',
    docSection: 'docs-music',
  }),
  // 🔴 TRIPS HAS NO CATALOG FEATURE AND IS NOT AN OMISSION. The trip service is
  // its own deployable (ADR-0005) reached over HTTP by the site's own Functions,
  // not a cog with slash commands, so there is nothing in the command catalog to
  // tie it to. `docSection` is the site route it lives at, and the panel is a
  // live read rather than a projection — which is why it gets LIVE and not an
  // as-of stamp.
  Object.freeze({ id: 'trips', feature: null, label: 'Trips', kind: 'trips', docSection: 'trips' }),
  // 🔴 P6-316 (REVIEW). `docLabel` IS THE WORDS OF A STUB'S DOCS LINK, AND `StubPanel`
  // ADDS THE ARROW. The design gives each stub its own — *How events work*, *How
  // hoops works* (`PortalHomeScreen3.dc.html` lines 344 and 345) — so none is built
  // from the heading any more. That is what rendered *How events works →* and
  // *How nba scores & stats works →*. A stub with a `docSection` and no `docLabel`
  // draws no link, and `src/test/portal/doc-links.test.jsx` fails for it.
  Object.freeze({
    id: 'events',
    feature: 'squadup',
    label: 'Events',
    kind: 'stub',
    docSection: 'docs-events',
    docLabel: 'How events work',
  }),
  // 🔴 P6-328b (GN-1b). HOOPS IS A REAL PANEL NOW, AND ITS DATA IS A PROJECTION THE BOT PUBLISHES
  // (`utils/hoops_projection.py` writes `3a/hoops/tonight.json`, `games/<id>.json` and `index.json`
  // into the bot's own bucket, read through `functions/api/hoops/`). It was a `stub` from P6-83 until
  // there was something to show; the cog and its catalog feature are unchanged, so the pill is still
  // derived from `commands.json` and still goes if the cog is shelved.
  //
  // ⚠️ `docLabel` WENT WITH THE STUB. It is the words a `StubPanel` draws for its docs link; a real
  // panel carries its own, as `WatchPanel` and `MoviePanel` do (*How hoops works →*, pinned by
  // `RULED_LABELS` in `doc-links.test.jsx`), so keeping the field here would be a second copy of the
  // sentence that nothing reads. `docSection` stays: `PortalHome` still derives `docPath` from it.
  Object.freeze({
    id: 'hoops',
    feature: 'hoops',
    label: 'Hoops',
    kind: 'hoops',
    docSection: 'docs-hoops',
  }),
])

/**
 * Catalog features that are deliberately not branches, and why.
 *
 * 🔴 REASONS, NOT AN IGNORE LIST. The point is that somebody decided, and that
 * the decision is readable by the next person — see the derivation note above.
 */
export const NOT_A_BRANCH = Object.freeze({
  // 🔴 JORDAN, 2026-09-03: *"shelve fantasy for now, will revisit closer to the
  // NBA season."* Shelving means the cog does not load and its commands do not
  // register — the treatment `cogs.hoopsickos` got in P6-44 — so there is no
  // fantasy branch to select and this is an ABSENCE, not a stub, not a
  // greyed-out entry and not a "coming back" note.
  //
  // ⚠️ IT IS LISTED HERE RATHER THAN LEFT TO THE DERIVATION ON PURPOSE, and the
  // ordering is the reason: the cog's own shelving is bot-lane work that may
  // land before or after this page. Until it does, `fantasy` is still in the
  // catalog and a purely derived list would render the pill. This entry makes
  // the portal correct on both sides of that merge, and it becomes inert — not
  // wrong — the moment the catalog stops carrying the feature.
  fantasy: 'shelved 2026-09-03, to be revisited closer to the NBA season',
  // 🔴 P6-4-C1, 2026-09-12. `cogs.cards` joined `COGS` and registers real
  // commands (`/cards`, `/cards admin channel`), so the catalog carries the
  // feature starting the moment that PR merges. The portal views — the
  // binder, the card page, the rip-night wall — are C4's, a separate,
  // not-yet-landed slice. So there is no panel for this branch to open onto
  // yet, and a purely derived list would render a pill pointing at nothing.
  //
  // ⚠️ LISTED HERE RATHER THAN LEFT TO THE DERIVATION ON PURPOSE, for the
  // same merge-ordering reason `fantasy` above is: the cog (bot-lane work)
  // and the pill (site-lane work, C4) are two different PRs, and this entry
  // is what keeps the portal correct on both sides of that gap. It is NOT
  // the `fantasy` shape otherwise — the cog is fully live, not shelved; the
  // gap is that this page has no view for it yet, not that the feature does
  // not exist.
  //
  // C4 removes this line and adds the `cards` row to `BRANCHES` in the same
  // PR that ships the panel. Until then this entry is what stands between
  // `unaccountedFeatures` and a red `Site Suite`.
  cards: 'the cards pill and panel are C4; this keeps the portal correct on both sides of that merge',
  // Not feature groups. Each is one utility command that belongs on the command
  // reference, and a portal branch for "Help" would be a panel about a page.
  help: 'one command, and it is the command reference itself',
  bug: 'one command; reporting a bug is not a place to visit',
  bug_loop: 'one command; it reports on bugs and ideas rather than being a feature',
  idea: 'one command; banking an idea is not a place to visit',
  // 🔴 ADMIN IS NOT A BRANCH AND MUST NOT BECOME ONE HERE. Spec §5 item 3 gives
  // the admin console its own surface, and §4.3 puts a hard boundary round it:
  // read-only, no actions, *"an admin console that mutates bot state"* is a
  // write path to a bot-owned store and needs its own ADR. It is explicitly out
  // of scope for this page.
  admin: 'its own read-only surface, spec §5 item 3 — deliberately not a tab here',
  // The music service's second feature group — charts, the canon, Song of the
  // Year. One deployable, one branch: `music` is the pill, and a second pill for
  // the same service would be a menu of internal groupings rather than of
  // places. Nothing from either is projected yet, so the panel says the same
  // sentence for both.
  social: 'the music service’s other feature group; the Music branch is the deployable’s',
  // W2b (P6-244) removed the `watch` entry that lived here: `cogs.watch`
  // registered its commands under W0, and this slice ships the pill and
  // panel that make it a real branch instead of a documented hole — see
  // `BRANCHES` above.
})

/**
 * The `!` surface of a feature that already has a slash surface.
 *
 * `scripts/generate-content.mjs` publishes these as their own catalog rows —
 * `movie-prefix`, `hoops-prefix` — and their own blurb says what they are:
 * *"The same feature, reached with a ! instead of a slash."* They are a SURFACE,
 * not a feature, so they are never a branch and they are never a hole on their
 * own account.
 *
 * ⚠️ THEY ARE STILL ACCOUNTED FOR THROUGH THEIR BASE, which is what keeps this
 * from being a blanket ignore. A `arcade-prefix` arriving with no `arcade`
 * anywhere is a hole and fails the gate — because that is a feature this file
 * has never been told about, wearing a suffix.
 */
const PREFIX_SURFACE = '-prefix'

function baseFeature(key) {
  return key.endsWith(PREFIX_SURFACE) ? key.slice(0, -PREFIX_SURFACE.length) : key
}

/**
 * The branches to offer, given the catalog's feature list.
 *
 * ⚠️ `features` IS PASSED IN rather than imported here, so the test can drive
 * this with a catalog that is MISSING one — which is the only way to know the
 * dropping actually works. A function that read its own input could only ever be
 * tested against today's answer.
 */
export function branchesFor(features) {
  const present = new Set(
    (Array.isArray(features) ? features : [])
      .map((feature) => feature?.key)
      .filter((key) => typeof key === 'string'),
  )
  return BRANCHES.filter((branch) => {
    if (Object.hasOwn(NOT_A_BRANCH, branch.id) || Object.hasOwn(NOT_A_BRANCH, branch.feature ?? ''))
      return false
    // A branch with no catalog feature is not a cog — see `trips` above.
    return branch.feature === null || present.has(branch.feature)
  })
}

/** One catalog feature row, or `null`. The panel's heading and paragraph. */
export function featureRow(features, key) {
  if (typeof key !== 'string') return null
  const rows = Array.isArray(features) ? features : []
  return rows.find((feature) => feature?.key === key) ?? null
}

/**
 * Features the catalog carries that this file has never been told about.
 *
 * The hole gotcha 1 prescribes. Exported so the test asserts on the HOLE rather
 * than on a count — a count moves every time anybody ships anything, and a test
 * keyed on one gets updated without being read.
 */
export function unaccountedFeatures(features) {
  const known = new Set([
    ...BRANCHES.map((branch) => branch.feature).filter(Boolean),
    ...Object.keys(NOT_A_BRANCH),
  ])
  return (Array.isArray(features) ? features : [])
    .map((feature) => feature?.key)
    // A `-prefix` row is accounted for by its BASE, never by its own name — see
    // PREFIX_SURFACE. That is what makes it a resolution rather than an excuse.
    .filter((key) => typeof key === 'string' && !known.has(baseFeature(key)))
    .sort()
}
